<template>
  <div class="table-container">
    <b-table
        :borderless="true"
        :empty-text="t('NO_RESULTS')"
        :fields="props.fields"
        :items="rows"
        :striped="true"
        :class="props.tableClass"
        :sort-by="currentSort.sortBy"
        :sort-desc="currentSort.sortDesc"
        @sortChanged="onSortChanged"
        responsive
        show-empty
    >
      <template v-for="(_, slotName) of $slots" v-slot:[slotName]="scope">
        <slot :name="slotName" v-bind="scope"/>
      </template>
    </b-table>

    <loader-container :active="loaderActive"/>
  </div>

  <b-pagination
      v-if="totalRows > props.rowsPerPage"
      v-model="currentPage"
      :per-page="props.rowsPerPage"
      :total-rows="totalRows"
      @page-click="onPageChange"
      align="center"
  />
</template>

<script setup lang="ts">
import {onMounted, ref} from "vue";
import type {BvEvent} from "bootstrap-vue";
import {useI18n} from "vue-i18n";
import LoaderContainer from "@/components/LoaderContainer.vue";

interface PaginationRequest {
  page: number;
  size: number;
  sort?: string;
  searchValue?: string;
}

interface PaginationResponse {
  data: {
    content: object[];
    totalSize: number;
  };
}

interface SortEvent {
  sortBy?: string;
  sortDesc?: boolean;
}

const {t} = useI18n();
const loaderActive = ref(false);

const props = withDefaults(defineProps<{
  tableClass?: string;
  rowsPerPage?: number;
  onPageChange: (data: PaginationRequest) => Promise<PaginationResponse>;
  fields: object[];
}>(), {
  rowsPerPage: 15,
});

const currentPage = ref(1);
const rows = ref<object[]>([]);
const totalRows = ref(0);
const searchTerm = ref("");
const currentSort = ref<SortEvent>({});

onMounted(async () => {
  await fetchRows();
});

const onSortChanged = async (sort: SortEvent) => {
  currentPage.value = 1;
  currentSort.value = sort;
  await fetchRows();
};

const onPageChange = async (_: BvEvent, page: number) => {
  currentPage.value = page;
  await fetchRows();
};

const onSearch = async (value: string) => {
  currentPage.value = 1;
  searchTerm.value = value;
  await fetchRows();
};

const reset = async () => {
  currentSort.value = {};
  currentPage.value = 1;
  searchTerm.value = "";
  await fetchRows();
};

const fetchRows = async () => {
  loaderActive.value = true;

  const params: PaginationRequest = {
    page: currentPage.value - 1,
    size: props.rowsPerPage,
  };

  if (currentSort.value.sortBy) {
    params["sort"] = `${currentSort.value.sortBy},${currentSort.value.sortDesc ? "desc" : "asc"}`;
  }

  if (searchTerm.value) {
    params["searchValue"] = searchTerm.value;
  }

  try {
    const resp = await props.onPageChange(params);
    rows.value = resp.data.content;
    totalRows.value = resp.data.totalSize;
  } finally {
    loaderActive.value = false;
  }
};

defineExpose({
  onSearch,
  reset,
});
</script>

<style lang="scss">

</style>
